import React, { useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    transform: translate(-50%, -48%) scale(0.95);
    opacity: 0;
  }
  to {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
`;

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.92);
  z-index: 99999;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.3s ease-out;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  cursor: pointer;
`;

const ModalContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90vw;
  max-width: 1200px;
  height: auto;
  max-height: 85vh;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
  animation: ${slideIn} 0.3s ease-out;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1);
  cursor: default;
  
  @media (max-width: 768px) {
    width: 95vw;
    max-height: 70vh;
    border-radius: 8px;
  }
  
  @media (max-width: 480px) {
    width: 100vw;
    height: 56.25vw;
    max-height: none;
    border-radius: 0;
  }
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  height: 0;
  background: #000;
`;

const VideoIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
`;

const CloseButton = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 300;
  line-height: 1;
  padding: 0;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
    border-color: rgba(255, 255, 255, 0.4);
  }
  
  &:focus {
    outline: 2px solid #ffd700;
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 20px;
    top: 15px;
    right: 15px;
  }
`;

const CloseHint = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: rgba(255, 255, 255, 0.8);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  pointer-events: none;
  z-index: 100000;
  
  @media (max-width: 768px) {
    font-size: 12px;
    padding: 6px 12px;
    bottom: 15px;
  }
`;

interface VideoModalProps {
  isOpen: boolean;
  videoUrl: string;
  onClose: () => void;
  title?: string;
}

export const VideoModal: React.FC<VideoModalProps> = ({ 
  isOpen, 
  videoUrl, 
  onClose,
  title 
}) => {
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      
      const focusTrap = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          e.preventDefault();
          const closeBtn = document.querySelector('[aria-label="Close video"]') as HTMLElement;
          if (closeBtn) closeBtn.focus();
        }
      };
      
      document.addEventListener('keydown', focusTrap);
      
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.removeEventListener('keydown', focusTrap);
        document.body.style.overflow = '';
      };
    }
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  const getEmbedUrl = (url: string) => {
    if (url.includes('vimeo.com')) {
      const videoId = url.split('/').pop()?.split('?')[0];
      return `https://player.vimeo.com/video/${videoId}?autoplay=1&autopause=0`;
    }
    
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      let videoId = '';
      if (url.includes('youtube.com/watch')) {
        videoId = url.split('v=')[1]?.split('&')[0] || '';
      } else if (url.includes('youtu.be')) {
        videoId = url.split('/').pop()?.split('?')[0] || '';
      }
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
    }
    
    return url;
  };

  return (
    <ModalOverlay 
      $isOpen={isOpen} 
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={title || "Video modal"}
    >
      <CloseButton 
        onClick={onClose}
        aria-label="Close video"
        type="button"
      >
        ✕
      </CloseButton>
      
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <VideoWrapper>
          <VideoIframe
            src={getEmbedUrl(videoUrl)}
            title={title || "Video player"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </VideoWrapper>
      </ModalContent>
      
      <CloseHint>Press ESC or click outside to close</CloseHint>
    </ModalOverlay>
  );
};